import { processDateJS } from "../../../../utils/dateProcessor";
import { formatNumber } from "../../../../utils/formatters";
import {
  blueColour,
  defaultFontBold,
  defaultFontItalic,
  defaultFontNormal,
} from "../utils/styling";
import { generateSectionParagraphs } from "../utils/WordExportUtils";

const electionDateNotAfterPoAEnd = (electionDate, poaEnd) => {
  return !processDateJS(electionDate)?.isAfter(processDateJS(poaEnd));
};

export const getNoDisallowanceParagraphs = (data) => {
  const {
    poaData,
    poaEnd,
    savedData,
    formattedAmendedFilingDeadline,
    groupDisallowance,
  } = data;

  if (groupDisallowance !== 0) return [];

  const paragraphs = [
    {
      condition: true,
      segments: [
        {
          text: "No disallowance",
          font: defaultFontBold,
          break: 1,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: "There has been no disallowance of Tax-Interest in the period under the ",
          font: defaultFontNormal,
        },
        {
          text: poaData.apply_group_ratio_election
            ? "Group Ratio"
            : "Fixed Ratio",
          font: defaultFontNormal,
        },
        {
          text: " method.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition:
        poaData.interest_capacity_de_minimis >
        poaData.group_interest_allowance + poaData.group_bf_interest_allowance,
      segments: [
        {
          text: "As the Interest Capacity of the group is less than the de-minimis threshold of £2m per annum, the de-minimis has been applied.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: "Unused Interest Allowance from prior periods",
          font: defaultFontItalic,
          break: 1,
        },
        {
          text: "The amount of disallowance in a period can be reduced where there is unused Interest Allowance brought forward from prior periods. Broadly, a group can carry forward any unused interest allowance for a period of 5 years, to be utilised in later periods where there would otherwise be a disallowance. In order to carry forward unused interest allowance a full interest restriction return must be submitted for each year between that in which the unused interest allowance is generated and that in which it is used.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition: poaData.group_unused_allowance === 0,
      segments: [
        {
          text: "There is no unused interest allowance brought forward to the current period.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "[It is recommended that the relationship team add some commentary to explain why there is no unused interest allowance brought forward to the current period. We would expect unused interest allowance to be available if in the prior year Interest Allowance exceeded Aggregate net tax-interest expense. It may also be available if this was the case in any of the 5 prior periods, depending on whether it has been used since it was generated. ",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
        {
          text: "Whilst the Corporate Interest Restriction Analyser will track Unused Interest Allowance for periods of account for which it is used, it is reliant on information entered in respect of periods before then. If you have any uncertainty regarding whether Interest Allowance was generated in prior periods, please contact a CIR specialist.",
          font: defaultFontNormal,
          underline: true,
          color: blueColour,
        },
        {
          text: "If an abbreviated return was submitted in the prior year the following wording could be used. If abbreviated returns were submitted in any of the five previous periods, appropriately adapted wording could be used to recognise that unused interest allowance may have arisen in periods when abbreviated returns were submitted (or the filing of a full return for those periods could allow for unused interest allowance to continue to be carried forward):",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
        {
          text: "As an abbreviated return was submitted in the prior period, there is no unused interest allowance brought forward. It is possible to submit a revised full interest restriction return for this period (and broadly up to the 5 previous periods) to generate unused interest allowance.]",
          font: defaultFontItalic,
          color: blueColour,
          break: 1,
        },
      ],
    },
    {
      condition: poaData.group_unused_allowance > 0,
      segments: [
        {
          text: "Unused interest allowance of £",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: formatNumber(poaData.group_unused_allowance),
          font: defaultFontNormal,
        },
        {
          text: " has been brought forward to the current period. Unused interest allowance can only be used in future periods if it has not already been used and is “unexpired”. Unused interest allowance generally expires 5 years after the end of the period and is used on a first in, first out basis. As such, it will be necessary to consider whether the allowance brought forward to the current period is still “unexpired” for the next period of account.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition:
        poaData.period_unused_interest_allowance - poaData.group_reactivations >
        0,
      segments: [
        {
          text: "Additional unused interest allowance of £",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: formatNumber(
            poaData.period_unused_interest_allowance -
              poaData.group_reactivations,
          ),
          font: defaultFontNormal,
        },
        {
          text: " has been generated in the period.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition:
        poaData.group_interest_allowance ===
        poaData.aggregate_net_tax_interest_expense,
      segments: [
        {
          text: "As Interest allowance is equal to Aggregate Net Tax-Interest expense in the current year, no additional unused interest allowance has been generated in the period, but equally no unused interest allowance brought forward has been utilised in the period.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.group_interest_allowance <
          poaData.aggregate_net_tax_interest_expense &&
        poaData.interest_capacity_de_minimis >
          poaData.group_interest_allowance +
            poaData.group_bf_interest_allowance &&
        poaData.group_bf_interest_allowance > 0,
      segments: [
        {
          text: "Whilst the interest capacity was calculated based on the de-minimis for the period, unused interest allowance brought forward to the current period of £",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: formatNumber(poaData.group_bf_interest_allowance),
          font: defaultFontNormal,
        },
        {
          text: " is still treated as used in the return, as interest allowance is used under s394 TIOPA 2010 when the ANTIE exceeds the Interest Allowance (which does not include the de-minimis interest deduction).",
          font: defaultFontNormal,
        },
        {
          text: " [Relationship team to confirm that they are comfortable no further unused interest allowance was available. Interest allowance may have been available for example if abbreviated returns had been submitted in the last 5 years.]",
          font: defaultFontNormal,
          color: blueColour,
        },
      ],
    },
    {
      condition:
        poaData.group_interest_allowance <
          poaData.aggregate_net_tax_interest_expense &&
        poaData.interest_capacity_de_minimis <=
          poaData.group_interest_allowance +
            poaData.group_bf_interest_allowance,
      segments: [
        {
          text: "Unused interest allowance brought forward to the current period of £",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: formatNumber(poaData.group_bf_interest_allowance),
          font: defaultFontNormal,
        },
        {
          text: " is used in the return as the interest allowance for the current period is less than the Aggregate Net Tax-Interest expense for the period. The use of this previously unused interest allowance has prevented a disallowance in the current period.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.group_reactivations > 0,
      segments: [
        {
          text: "As the Interest Allowance exceeds the Tax-Interest expense for the period, part of the group’s previously disallowed Tax-Interest expense has been reactivated in the period.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "Allocation of reactivations",
          font: defaultFontItalic,
          break: 2,
        },
        {
          text: "In general, reactivations are required to be brought into account in the earliest accounting period of a company that overlaps with the period of account of the worldwide group. They can be freely allocated between group companies up to the “amount available for reactivation” of each company. This is often the disallowed tax-interest amount carried forward, but care is needed where a company is acquired in the period or has an accounting period that is not coterminous with the period of account of the group.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "In the absence of an election, where the amount to be reactivated is less than a company's total of Disallowed Tax-Interest expense amounts brought forward, the legislation requires that Disallowed Tax-Interest expense amounts are reactivated in a specific order. However, a company can specify which particular Disallowed Tax-Interest expense amounts are reactivated in an accounting period by making an election under s380 in its company tax return. See Appendix 2 for further details.",
          font: defaultFontNormal,
          break: 2,
        },
        {
          text: "The allocation of the reactivations is important, as the disallowed Tax-Interest expense amounts carried forward are company attributes (not group attributes) and can be lost in future periods under certain circumstances, including:",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    ...(poaData.group_interest_reactivation_cap > 0
      ? [
          {
            condition: true,
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "For trading disallowances - where the company ceases to trade or its trade becomes small or negligible, including where, for example, that trade is transferred within a group (for disallowed Tax-Interest amounts that would otherwise have been trading deductions);",
                font: defaultFontNormal,
              },
            ],
          },
          {
            condition: true,
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "For non-trading disallowances - where the company ceases to carry on an investment business or that investment business becomes small or negligible (for disallowed Tax-Interest amounts that would otherwise have been non-trading deductions); and",
                font: defaultFontNormal,
              },
            ],
          },
          {
            condition: true,
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "For non-trading disallowances - on a change in ownership of a company, where certain other “Change in Ownership” conditions in Part 14, CTA 2010 are met:",
                font: defaultFontNormal,
              },
            ],
          },
          {
            condition: true,
            bullet: "dot",
            bullet_level: 1,
            segments: [
              {
                text: "Where the conditions in Chapters 3, 4 or 5A relating to relevant non-trading debits are met, such that the use of non-trading debits (which include non-trading CIR disallowances determined on an amortised cost basis of accounting) are restricted in the period. This most commonly occurs where there is a significant increase in capital and/or a major change in the nature of conduct of the investment business of the company; and",
                font: defaultFontNormal,
              },
            ],
          },
          {
            condition: true,
            bullet: "dot",
            bullet_level: 1,
            segments: [
              {
                text: 'Where, within an effective five or eight year window around the change of ownership, there is a "major change" in the trade or business of the company or a “co-transferred company” such that the use of relevant non-trading debits are restricted under the application of s676AG CTA 2010 within Chapter 2A.',
                font: defaultFontNormal,
              },
            ],
          },
        ]
      : []),
    {
      condition: poaData.group_interest_reactivation_cap > 0,
      segments: [
        {
          text: "Whilst the allocation of reactivations can be amended through submitting an amended return before ",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: `[${formattedAmendedFilingDeadline}]`,
          font: defaultFontNormal,
          color: blueColour,
        },
        {
          text: " it is recommended that, where possible, any amended return is submitted on or before the expiry of the deadline for amending any relevant company tax returns (typically 2 years from the end of the accounting period, but potentially extended by enquiries and certain other provisions) to ensure that group relief and other group attributes can be amended at the same time.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: "Application of Group Ratio",
          font: defaultFontItalic,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance === poaData.fr_debt_cap,
      segments: [
        {
          text: "As the allowance is driven by the fixed ratio debt cap, we would not expect a group ratio election to result in an increase in interest allowance.",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance < poaData.fr_debt_cap,
      segments: [
        {
          text: "If the ratio of Group Interest to Group EBITDA (both as defined) of the worldwide group is more than 30%, it is possible that the interest allowance could be increased, were the group to make a Group Ratio election (See Appendix 2).",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance < poaData.fr_debt_cap &&
        poaData.group_bf_interest_allowance > 0,
      segments: [
        {
          text: "This could decrease the amount of unused interest allowance used in the period.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance < poaData.fr_debt_cap &&
        poaData.group_bf_interest_allowance === 0,
      segments: [
        {
          text: `This could create ${poaData.period_unused_interest_allowance > 0 ? "additional " : ""}unused interest allowance to carry forward to future periods.`,
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance < poaData.fr_debt_cap,
      segments: [
        {
          text: "The application of the Group Ratio election requires detailed analysis, including an analysis of the Group Interest expense, not least as certain debt including related party debt is excluded from the calculation.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "[Relationship team to insert short summary of the work undertaken to conclude that Group Ratio is unlikely to be beneficial or cost effective - e.g. because all Group debt is from related parties, because a high-level calculation indicates it would be significantly below 30% or because no disallowances are anticipated in future periods] / [You have informed us that you do not intend to make a Group Ratio election [due to the additional work required to review the position] / [as you do not anticipate the group ratio would exceed 30%] / [as you do not currently expect disallowances in future periods, so do not need the additional unused interest allowance].",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance === poaData.fr_debt_cap &&
        poaData.aggregate_tax_ebitda <= poaData.interest_capacity_de_minimis,
      segments: [
        {
          text: "As the Aggregate Tax-EBITDA is less than the de-minimis amount, it is not possible to reduce the disallowance in the period, as the Interest Allowance under the group ratio cannot exceed the de-minimis even if the group ratio was 100%.",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition:
        poaData.apply_group_ratio_election === false &&
        poaData.group_interest_allowance === poaData.fr_debt_cap &&
        poaData.aggregate_tax_ebitda <= poaData.interest_capacity_de_minimis &&
        poaData.group_bf_interest_allowance > 0,
      segments: [
        {
          text: "However, as unused interest allowance brought forward to the current period of £",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: formatNumber(poaData.group_bf_interest_allowance),
          font: defaultFontNormal,
        },
        {
          text: " is used in the period, it is possible that the amount of unused interest allowance used in the period could be reduced were the group to make a Group Ratio election (See Appendix 2). The application of the Group Ratio election requires detailed analysis, including an analysis of the Group Interest expense, not least as certain debt including related party debt is excluded from the calculation of both the Group Ratio and the Group Ratio debt cap.",
          font: defaultFontNormal,
        },
        {
          text: "[Relationship team to insert short summary of the work undertaken to conclude that Group Ratio is unlikely to be beneficial - e.g. because all Group debt is from related parties, or because a high-level calculation indicates it would be significantly below 30%] / [You have informed us that you do not intend to make a Group Ratio election [due to the additional work required to review the position] / [as you are comfortable that it would not reduce the amount of disallowance].",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
      ],
    },
    ...(poaData.apply_group_ratio_election === true
      ? [
          {
            condition: true,
            segments: [
              {
                text: "There are a number of elections that can be made to modify the application of the CIR rules under the Group Ratio method, which are set out in Appendix 2, many of which can have a substantial impact on the calculation of the Group Ratio. The following elections have been made by the group:",
                font: defaultFontNormal,
                break: 1,
              },
            ],
          },
          {
            condition: poaData?.group_ratio_blended_election,
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "Group ratio blended election",
                font: defaultFontNormal,
                color: blueColour,
              },
            ],
          },
          {
            condition:
              savedData?.interest_allowance_alt_calc &&
              electionDateNotAfterPoAEnd(
                savedData?.interest_allowance_alt_calc_year,
                poaEnd,
              ),
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "Interest allowance (alternative calculation) election",
                font: defaultFontNormal,
                color: blueColour,
              },
            ],
          },
          {
            condition:
              savedData?.interest_allowance_consolidated_partnership &&
              electionDateNotAfterPoAEnd(
                savedData?.interest_allowance_consolidated_partnership_year,
                poaEnd,
              ),
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "Interest allowance (consolidated partnership) election",
                font: defaultFontNormal,
                color: blueColour,
              },
            ],
          },
          {
            condition:
              poaData?.election_interest_allowance_non_consolidated_investment,
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "Interest allowance (non-consolidated investment) election",
                font: defaultFontNormal,
                color: blueColour,
              },
            ],
          },
          {
            condition:
              savedData?.group_ebitda_gains &&
              electionDateNotAfterPoAEnd(
                savedData?.group_ebitda_gains_year,
                poaEnd,
              ),
            bullet: "dot",
            bullet_level: 0,
            segments: [
              {
                text: "Group EBITDA (chargeable gains) election",
                font: defaultFontNormal,
                color: blueColour,
              },
            ],
          },
          {
            condition: true,
            segments: [
              {
                text: "[Relationship team to insert short summary of any work undertaken to consider the impact of elections that have not been made. It is recommended that where there is a disallowance the application of the Interest allowance (alternative calculation) election is considered as a minimum.] / [The potential impact of any additional Group Ratio elections falls outside the scope of our review - please ensure that you review the additional Group Ratio elections in Appendix 2 and let us know if there are any that you would like to discuss further].",
                font: defaultFontNormal,
                color: blueColour,
                break: 1,
              },
            ],
          },
        ]
      : []),
  ];

  return [...generateSectionParagraphs(paragraphs)];
};
